import { Modal } from '../../../../../../mini-apps/_shared/components/Modal';

/**
 * "How Ranking Works" modal (bead .17, U7) — copied from Action View's own
 * `RankingLogicModal`, `mini-apps/action-view/components/ActionToolbar.tsx:8-55`
 * (copy, never import, across the mini-app boundary — CLAUDE.md). The
 * structure (manual/auto tiers, sort-within-tier, sequential numbering) is
 * Action View's; the wording diverges wherever THIS tab's comparator
 * diverges, which round-1 fix R23 corrected:
 *
 * - "set by admins in Settings via drag-to-rank" -> "set by admins in the
 *   Ranker" (the Terra Signals equivalent of that UI).
 * - The "Fit score" tiebreak is GONE. `prioritizeSignals.ts` has no
 *   fit-score term — the field does not exist on a Signals row — and the
 *   step-number / step-type / Ranker-entry-order / overdue / surfaced
 *   chain listed here is the comparator that actually runs.
 * - Deactivated types do not get a "U" badge here; they are excluded by
 *   the SQL universe (`signalsUniverseWhere`), so the honest sentence is
 *   "hidden from every Signals view".
 *
 * Keep this copy in lockstep with `prioritizeSignals.ts`'s comparator — a
 * rep reads it to predict their own #1.
 *
 * Imports `Modal` from its own file, NOT the `mini-apps/_shared/components`
 * barrel (`index.ts`) — that barrel re-exports `./flow.ts`, which
 * re-exports `SignalsFlowTable` itself (core-owned Flow components,
 * re-exported for mini-app consumers), so importing the barrel from
 * anywhere under `signals/` is a real dependency cycle
 * (`import/no-cycle`), not a false positive.
 */
export function RankingLogicModal({ onClose }: { onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title="How Ranking Works">
      <div className="space-y-4 text-sm text-nightshade-700 dark:text-nightshade-300">
        <p>
          Each rep&apos;s actions are ranked <strong>independently</strong> — your #1 is always your most
          important task, regardless of which view or filters you use.
        </p>

        <div>
          <h4 className="font-semibold text-nightshade-900 dark:text-nightshade-100 mb-1">Step 1 — Step Type Tiers</h4>
          <p>Actions are split into two tiers. Manual steps always rank above auto steps.</p>
          <ul className="list-disc ml-5 mt-1 space-y-0.5">
            <li><strong>Manual</strong> — Call, Email, LinkedIn, Task, or no Gong flow</li>
            <li><strong>Auto</strong> — Auto Email (Gong sends automatically)</li>
          </ul>
          <p className="mt-1 text-nightshade-500 dark:text-nightshade-400 text-xs">
            Closed / archived actions are unranked.
          </p>
        </div>

        <div>
          <h4 className="font-semibold text-nightshade-900 dark:text-nightshade-100 mb-1">Step 2 — Sort Within Each Tier</h4>
          <p>Within manual and auto tiers, actions are sorted by these factors in order:</p>
          <ol className="list-decimal ml-5 mt-1 space-y-0.5">
            <li><strong>Signal type priority</strong> — set by admins in the Ranker</li>
            <li><strong>Step number</strong> — earlier steps in the flow rank higher</li>
            <li><strong>Step type</strong> — Call, then Manual email, then LinkedIn, and so on</li>
            <li><strong>The Ranker&apos;s entry order</strong> — for anything still tied</li>
            <li><strong>Task due date</strong> — overdue first, and the most overdue first among those</li>
            <li><strong>When it surfaced</strong> — newest surfaced first</li>
          </ol>
        </div>

        <div>
          <h4 className="font-semibold text-nightshade-900 dark:text-nightshade-100 mb-1">Step 3 — Number Assignment</h4>
          <p>
            After sorting, each rep&apos;s actions are numbered sequentially: <strong>1, 2, 3…</strong> starting
            from their most important task. These numbers stay the same across My Actions and All Actions
            views.
          </p>
        </div>

        <div className="border-t border-nightshade-200 dark:border-nightshade-700 pt-3 text-xs text-nightshade-500 dark:text-nightshade-400">
          <strong>Deactivated types</strong> (switched off in the Ranker) are hidden from every Signals view.
        </div>
      </div>
    </Modal>
  );
}
